import { useState, useEffect, Suspense, lazy, useMemo } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { Navigation } from "./components/layout/Navigation";
const Footer = lazy(() => import("./components/layout/Footer"));
const MobileBottomNav = lazy(() => import("./components/layout/MobileBottomNav"));
import ScrollToTop from "@/components/ScrollToTop";
import SEOJsonLD from "@/components/layout/SEOJsonLD";
import { fetchData } from "@/lib/supabase-function";

// ✅ React Query client memoized (prevents recreation)
const queryClient = new QueryClient();

// ✅ Lazy-load pages (Code Splitting)
const Index = lazy(() => import("./pages/Index"));
const Services = lazy(() => import("./pages/Services"));
const Cybersecurity = lazy(() => import("./pages/services/Security"));
const Solutions = lazy(() => import("./pages/Solutions"));
const About = lazy(() => import("./pages/About"));
const NotFound = lazy(() => import("./pages/NotFound"));
const CaseStudies = lazy(() => import("./pages/CaseStudies"));
const Blog = lazy(() => import("./pages/Blog"));
const Contact = lazy(() => import("./pages/Contact"));

const App = () => {
  const [darkMode, setDarkMode] = useState(false);
  const [menus, setMenus] = useState<any[]>([]);

  // ✅ Optimize theme + data load
  useEffect(() => {
    const savedTheme = localStorage.getItem("Core-theme");
    const prefersDark = window.matchMedia("(prefers-color-scheme: dark)").matches;
    const isDark = savedTheme === "dark" || (!savedTheme && prefersDark);

    document.documentElement.classList.toggle("dark", isDark);
    setDarkMode(isDark);

    // Prefetch critical menu data (async but non-blocking)
    
    fetchData("menumst", "menu_id, menu, active", "menu_id", "active", true)
      .then(({ data }) => setMenus(data || []))
      .catch(() => {});

    // ✅ Register service worker async after load
    if ("serviceWorker" in navigator) {
      window.addEventListener("load", () => {
        navigator.serviceWorker.register("/sw.js");
      });
    }
  }, []);

  // ✅ useMemo for stable toggle function reference
  const toggleDarkMode = useMemo(
    () => () => {
      const newMode = !darkMode;
      setDarkMode(newMode);
      document.documentElement.classList.toggle("dark", newMode);
      localStorage.setItem("Core-theme", newMode ? "dark" : "light");
    },
    [darkMode]
  );

  return (
    <QueryClientProvider client={queryClient}>
      <TooltipProvider delayDuration={100}>
        <Toaster />
        <Sonner />
        <BrowserRouter>
          <div className="min-h-screen bg-background text-foreground">
            {/* 🧭 Header loads first for faster LCP */}
            <Navigation darkMode={darkMode} toggleDarkMode={toggleDarkMode} />

            <main id="main-content">
              <ScrollToTop />
              <SEOJsonLD />

              {/* 🌀 Suspense boundary with minimal fallback */}
              <Suspense
                fallback={
                  <div
                    style={{
                      height: "100vh",
                      width: "100%",
                      background: "#f9fafb",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontSize: "1.25rem",
                    }}
                  >
                    Loading...
                  </div>
                }
              >
                <Routes>
                  <Route path="/" element={<Index />} />
                  <Route path="/services" element={<Services />} />
                  <Route path="/services/:slug" element={<Cybersecurity />} />
                  <Route path="/solutions" element={<Solutions />} />
                  <Route path="/solutions/:slug" element={<Cybersecurity />} />
                  <Route path="/about" element={<About />} />
                  <Route path="/case-studies" element={<CaseStudies />} />
                  <Route path="/blog" element={<Blog />} />
                  <Route path="/contact" element={<Contact />} />
                  <Route path="*" element={<NotFound />} />
                </Routes>
              </Suspense>
            </main>

            {/* 🦶 Footer below-the-fold — low priority */}
            <Footer />
            <MobileBottomNav />
          </div>
        </BrowserRouter>
      </TooltipProvider>
    </QueryClientProvider>
  );
};

export default App;
